import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { useTheme } from '../../context/ThemeContext';
import { COLORS, COLORS_DARK } from '../../constants';
import useResponsiveChart from '../../hooks/useResponsiveChart';
import CustomTooltip from '../CustomTooltip';
import CustomLegend from '../CustomLegend';
import ExplanationCard from '../ExplanationCard';

const BarChartView = ({ selectedMethod, selectedModel, chartData, selectedLanguages }) => {
  const { isDarkMode } = useTheme();
  const colors = isDarkMode ? COLORS_DARK : COLORS;
  const { chartHeight, chartMargin, axisLabelFontSize, tickFontSize, isMobile } = useResponsiveChart();

  // Convert chartData format to match original (documents -> docCount)
  const barData = chartData.map(item => ({
    docCount: item.documents,
    ...Object.fromEntries(
      selectedLanguages.map(lang => [lang, item[lang]])
    )
  }));

  return (
    <div className="mb-8">
      <ExplanationCard title="Bar Chart Analysis Explanation">
        <p><strong>Bar Chart:</strong> Compares performance scores across different document counts for each language.</p>
        <p><strong>Each Bar Group:</strong> Represents a specific document count.</p>
        {/* <p><strong>Color Coding:</strong> Each language has a distinct color for easy comparison across document counts.</p> */}
        {/* <p><strong>Interpretation:</strong> Taller bars indicate better performance. Compare bar heights within groups to see language differences at each document count.</p> */}
      </ExplanationCard>

      <h3 className="text-3xl font-bold mb-3 text-center" style={{ color: 'var(--text-primary)' }}>
        Performance Bar Analysis
      </h3>
      <p className="text-lg text-center mb-8" style={{ color: 'var(--text-secondary)' }}>
        Compare performance across document counts for {selectedMethod} method - {selectedModel} model.
      </p>

      <div className="chart-wrapper">
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart data={barData} margin={chartMargin}>
            <CartesianGrid strokeDasharray="4 4" stroke={isDarkMode ? '#333333' : '#e0e7ff'} />
            <XAxis
              dataKey="docCount"
              label={{ value: 'Number of Documents', position: 'bottom', offset: 40, fill: isDarkMode ? '#a3a3a3' : '#4a5568', fontSize: axisLabelFontSize, fontWeight: 'bold' }}
              tickLine={false}
              axisLine={{ stroke: isDarkMode ? '#404040' : '#cbd5e0' }}
              padding={{ left: 15, right: 15 }}
              tick={{ fill: isDarkMode ? '#a3a3a3' : '#4a5568', fontSize: tickFontSize }}
            />
            <YAxis
              label={{ value: 'Performance Score', angle: -90, position: 'insideLeft', offset: 10, fill: isDarkMode ? '#a3a3a3' : '#4a5568', fontSize: axisLabelFontSize, fontWeight: 'bold' }}
              tickLine={false}
              axisLine={{ stroke: isDarkMode ? '#404040' : '#cbd5e0' }}
              domain={['dataMin - 10', 'dataMax + 10']}
              tick={{ fill: isDarkMode ? '#a3a3a3' : '#4a5568', fontSize: tickFontSize }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend content={<CustomLegend />} wrapperStyle={{ paddingTop: '20px' }} />
            {selectedLanguages.map(lang => (
              <Bar
                key={lang}
                dataKey={lang}
                fill={colors[lang]}
                name={lang}
                barSize={isMobile ? 15 : 30}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default BarChartView;